import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Save, Loader2, Eye, EyeOff, ShieldAlert,
  LayoutDashboard, Calendar, Users, FolderOpen, Scale, FileSpreadsheet,
  ClipboardList, UserCog, Bot, FileText, MessageSquare, Receipt, Mail,
  FileCode, Calculator, Zap, MessagesSquare
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Mapa de iconos por moduleId (sincronizado con TopNavigation.jsx)
const MODULE_ICONS = {
  'panel': LayoutDashboard,
  'calendario': Calendar,
  'contactos': Users,
  'expedientes': FolderOpen,
  'sentencias': Scale,
  'auditoria-expedientes': FileSpreadsheet,
  'tareas': ClipboardList,
  'rrhh': UserCog,
  'chat': Bot,
  'informes': FileText,
  'comunicaciones': MessageSquare,
  'contabilidad': Receipt,
  'correo': Mail,
  'plantillas': FileCode,
  'calculadora': Calculator,
  'automatizacion': Zap,
  'chat_interno': MessagesSquare,
};

export default function ConfiguracionModulosMenu() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disponibles, setDisponibles] = useState([]);
  const [deshabilitados, setDeshabilitados] = useState(new Set());
  const [isAdmin, setIsAdmin] = useState(false);

  // Cargar usuario actual
  useEffect(() => {
    try {
      const userData = localStorage.getItem('lexduo_user');
      if (userData) {
        const user = JSON.parse(userData);
        setIsAdmin(user.rol === 'admin' || user.email === 'admin@lexduo.cat');
      }
    } catch (e) { /* noop */ }
  }, []);

  // Cargar lista del backend
  useEffect(() => {
    const token = localStorage.getItem('lexduo_token');
    if (!token) return;
    setLoading(true);
    axios.get(`${API}/api/config/modulos-menu`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        setDisponibles(res.data?.disponibles || []);
        setDeshabilitados(new Set(res.data?.deshabilitados || []));
      })
      .catch(err => {
        console.error('Error cargando modulos:', err);
        toast({ title: 'Error', description: 'No se pudo cargar la configuración', variant: 'destructive' });
      })
      .finally(() => setLoading(false));
  }, []);

  const toggleModulo = (moduleId) => {
    setDeshabilitados(prev => {
      const next = new Set(prev);
      if (next.has(moduleId)) next.delete(moduleId);
      else next.add(moduleId);
      return next;
    });
  };

  const handleGuardar = async () => {
    const token = localStorage.getItem('lexduo_token');
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/config/modulos-menu`,
        { deshabilitados: Array.from(deshabilitados) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({
        title: 'Configuración guardada',
        description: 'Los cambios se aplicarán al recargar la página.'
      });
      try { sessionStorage.removeItem('lexduo_chat_interno_activo'); } catch (e) { /* noop */ }
      // Recarga para reflejar el menú nuevo
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Error desconocido';
      toast({ title: 'Error al guardar', description: detail, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="container max-w-4xl mx-auto p-6">
        <Alert variant="destructive" data-testid="alert-no-admin">
          <ShieldAlert className="h-4 w-4" />
          <AlertDescription>
            Solo los usuarios con rol <strong>administrador</strong> pueden gestionar la visibilidad global de módulos.
          </AlertDescription>
        </Alert>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/configuracion')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Volver
        </Button>
      </div>
    );
  }

  const totalActivos = disponibles.length - deshabilitados.size;

  return (
    <div className="container max-w-4xl mx-auto p-6 space-y-6" data-testid="page-modulos-menu">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/configuracion')} className="mb-2 -ml-2" data-testid="btn-volver">
            <ArrowLeft className="h-4 w-4 mr-1" /> Volver a Configuración
          </Button>
          <h1 className="text-2xl font-bold text-gray-800">Visibilidad de módulos del menú</h1>
          <p className="text-sm text-gray-500 mt-1">
            Activa o desactiva globalmente las opciones del menú superior para todo el despacho.
            Los permisos individuales por usuario siguen aplicándose dentro de los módulos activos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {totalActivos} de {disponibles.length} activos
          </Badge>
        </div>
      </div>

      <Alert className="bg-amber-50 border-amber-200 text-amber-900">
        <ShieldAlert className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-900">
          Esta configuración afecta a <strong>todos los usuarios</strong>, incluido tú mismo.
          Si desactivas un módulo, desaparecerá del menú superior para todos.
          Tu acceso a esta página sigue disponible desde el menú lateral de Configuración.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Módulos disponibles</CardTitle>
          <CardDescription>Click en el switch para activar / desactivar cada opción del menú.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {disponibles.map(m => {
                const Icon = MODULE_ICONS[m.id] || FileText;
                const activo = !deshabilitados.has(m.id);
                return (
                  <label
                    key={m.id}
                    className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border transition-colors cursor-pointer ${
                      activo
                        ? 'bg-white border-gray-200 hover:bg-gray-50'
                        : 'bg-gray-100 border-gray-200 text-gray-500'
                    }`}
                    data-testid={`module-row-${m.id}`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Icon className={`h-4 w-4 flex-shrink-0 ${activo ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span className={`text-sm ${activo ? 'font-medium text-gray-800' : ''}`}>{m.label}</span>
                      {!activo && (
                        <Badge variant="outline" className="text-[10px] ml-1 bg-white">
                          <EyeOff className="h-2.5 w-2.5 mr-1" /> Oculto
                        </Badge>
                      )}
                    </div>
                    <Switch
                      checked={activo}
                      onCheckedChange={() => toggleModulo(m.id)}
                      data-testid={`switch-${m.id}`}
                    />
                  </label>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          variant="outline"
          onClick={() => navigate('/configuracion')}
          disabled={saving}
          data-testid="btn-cancelar"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleGuardar}
          disabled={saving || loading}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="btn-guardar"
        >
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Guardar cambios
        </Button>
      </div>
    </div>
  );
}
